import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface IncidentRow {
  readonly incidentId: string;
  readonly incidentCode: string;
  readonly title: string;
  readonly severity: string;
  readonly status: string;
  readonly detectedAt: string | null;
}

const SEVERITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
const STATUSES = ['', 'REPORTED', 'CLASSIFIED', 'ASSIGNED', 'INVESTIGATING', 'CONTAINED', 'RESOLVED', 'CLOSED', 'POST_REVIEW'];

/**
 * GR L30.02 — Security Incidents (`/security/incidents`). Case list of the
 * `security_incident` register with severity and lifecycle status, each opening
 * its incident workspace. Filterable by severity and status.
 */
export function GrIncidentsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: IncidentRow[] }>(`/v1/security/incidents${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, severity, status]);

  return (
    <main aria-label="security-incidents">
      <h1>{t('gr30.incidents.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.incidents.labelSeverity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.incidents.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.incidents.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.incidents.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.incidents.loading')}</p> : (
        <>
          <p aria-label="incident-count">{t('gr30.incidents.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.incidents.empty')}</p> : (
            <table aria-label="incident-table">
              <thead><tr><th>{t('gr30.incidents.colCode')}</th><th>{t('gr30.incidents.colTitle')}</th><th>{t('gr30.incidents.colSeverity')}</th><th>{t('gr30.incidents.colStatus')}</th><th></th></tr></thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.incidentId}>
                    <td>{i.incidentCode}</td><td>{i.title}</td><td>{i.severity}</td><td>{i.status}</td>
                    <td><Link aria-label={`open-${i.incidentId}`} to={`/security/incidents/${i.incidentId}`}>{t('gr30.incidents.open')}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
